/**
 * ENG-5801 — register/unregister the impersonation statusLine in the
 * operator's project dir.
 *
 * `connect` calls `registerStatusLine(projectCwd)` after registering the
 * SessionStart introduce hook. It upserts a `statusLine` key into
 * `<projectCwd>/.claude/settings.local.json` that runs the bundled shell
 * script `~/.augmented-impersonate/statusline.sh` (installed on first
 * connect from the CLI asset at `<package-root>/assets/impersonate-
 * statusline.sh`).
 *
 * While impersonation is active, the project-level `statusLine` overrides
 * the operator's user-level `~/.claude/settings.json` `statusLine` (Local
 * scope > User scope per Claude Code's settings precedence). This is an
 * intentional, ephemeral trade-off — `exit` restores the operator's prior
 * settings.local.json byte-for-byte from a backup, so their user-level
 * statusLine becomes effective again the moment the session ends.
 *
 * The backup mechanism is shared with `registerIntroduceHook` (both
 * features touch the same settings.local.json). The existence guard
 * `if (!existsSync(backupPath))` in each register makes the call order
 * irrelevant — whichever runs first captures the operator's true original;
 * whichever unregister runs first restores it; the other side is then a
 * no-op.
 */

import {
  chmodSync,
  copyFileSync,
  existsSync,
  mkdirSync,
  readdirSync,
  readFileSync,
  renameSync,
  rmdirSync,
  unlinkSync,
  writeFileSync,
} from 'node:fs';
import { homedir } from 'node:os';
import { dirname, join } from 'node:path';
import { fileURLToPath } from 'node:url';

import { BACKUP_SUFFIX } from './impersonate-fs-ops.js';
import type { StatusLineBackup } from './impersonate-state.js';

/**
 * Where we install the runtime statusline script on first connect. Lives
 * outside the operator's project so a `rm -rf .claude` doesn't kill it
 * for subsequent impersonations. The path is what
 * `<projectCwd>/.claude/settings.local.json` references as `statusLine`.
 */
export const INSTALLED_STATUSLINE_PATH = join(
  homedir(),
  '.augmented-impersonate',
  'statusline.sh',
);

const STATUS_LINE_KEY = 'statusLine';
const STATUS_LINE_COMMAND_TYPE = 'command';

/**
 * Install the bundled statusline asset to INSTALLED_STATUSLINE_PATH if
 * it isn't already there OR if its content differs from the bundled
 * asset. Recopying on content drift lets a new CLI release ship a fixed
 * script without operators having to manually delete the stale copy —
 * matches the "ship → next self-update applies it" loop the rest of the
 * codebase assumes. Exported so tests can drive it against a tmp HOME.
 */
export function installStatusLineAsset(
  targetPath: string = INSTALLED_STATUSLINE_PATH,
): void {
  mkdirSync(dirname(targetPath), { recursive: true });
  const source = resolveBundledAssetPath();
  const needsCopy =
    !existsSync(targetPath) ||
    readFileSync(source, 'utf-8') !== readFileSync(targetPath, 'utf-8');
  if (needsCopy) {
    copyFileSync(source, targetPath);
    chmodSync(targetPath, 0o755);
  }
}

/**
 * Upsert the impersonation statusLine into
 * `<projectCwd>/.claude/settings.local.json`. Returns a StatusLineBackup
 * the manifest stores so `exit` can undo exactly what was done.
 *
 * Idempotent: a repeat call when our statusLine is already registered is
 * a no-op (the existing backup is preserved). Doesn't clobber a backup
 * left behind by an earlier `registerIntroduceHook` — the existence
 * guard wins.
 */
export function registerStatusLine(
  projectCwd: string,
  options: { installAsset?: boolean } = {},
): StatusLineBackup {
  // Tests pass `installAsset: false` to avoid writing into the real
  // ~/.augmented-impersonate/ during the suite. Production callers
  // (connect) accept the default.
  if (options.installAsset !== false) {
    installStatusLineAsset();
  }

  const claudeDir = join(projectCwd, '.claude');
  const createdClaudeDir = !existsSync(claudeDir);
  mkdirSync(claudeDir, { recursive: true });

  const settingsPath = join(claudeDir, 'settings.local.json');
  const settingsExisted = existsSync(settingsPath);
  const backupPath = settingsPath + BACKUP_SUFFIX;

  let settings: Record<string, unknown> = {};
  if (settingsExisted) {
    // Back up the original verbatim — but ONLY if a sibling
    // registerIntroduceHook didn't already do it. Whichever ran first
    // captures the operator's true pre-impersonation state.
    if (!existsSync(backupPath)) {
      copyFileSync(settingsPath, backupPath);
    }
    settings = asPlainObject(safeParseJson(readFileSync(settingsPath, 'utf-8')));
  }

  settings[STATUS_LINE_KEY] = {
    type: STATUS_LINE_COMMAND_TYPE,
    command: INSTALLED_STATUSLINE_PATH,
  };

  writeFileSync(settingsPath, JSON.stringify(settings, null, 2) + '\n');

  return {
    settings_path: settingsPath,
    settings_backup: settingsExisted ? 'had-file' : 'didnt-exist',
    created_claude_dir: createdClaudeDir,
  };
}

/**
 * Reverse registerStatusLine: restore the backed-up settings file (or
 * remove the one we created) and drop the `.claude` dir if we created it
 * and it's now empty. Best-effort and idempotent — safe to call twice,
 * and safe to interleave with unregisterIntroduceHook in either order.
 */
export function unregisterStatusLine(backup: StatusLineBackup): void {
  const { settings_path, settings_backup, created_claude_dir } = backup;
  const backupPath = settings_path + BACKUP_SUFFIX;

  if (settings_backup === 'had-file') {
    // Restore the original over our merged version. If the sibling
    // unregisterIntroduceHook beat us to it, the backup is already gone
    // and the file is already restored — guarded.
    if (existsSync(backupPath)) {
      renameSync(backupPath, settings_path);
    } else if (existsSync(settings_path)) {
      // No backup left to restore — either it's already been restored by
      // the sibling hook unregister, OR we never had a backup because the
      // file was malformed at register time. Either way, the safe thing
      // is to strip just our statusLine key from the current file so we
      // don't leave a dangling pointer to INSTALLED_STATUSLINE_PATH if
      // the operator later deletes ~/.augmented-impersonate.
      stripStatusLineKey(settings_path);
    }
  } else {
    // We created the file — remove it (and the stray backup, if any).
    if (existsSync(settings_path)) unlinkSync(settings_path);
    if (existsSync(backupPath)) unlinkSync(backupPath);
  }

  if (created_claude_dir) {
    const claudeDir = dirname(settings_path);
    try {
      if (existsSync(claudeDir) && readdirSync(claudeDir).length === 0) {
        rmdirSync(claudeDir);
      }
    } catch {
      // Non-empty (operator added files) or already gone — leave it.
    }
  }
}

/**
 * Walk up from this module's location to find the bundled
 * `assets/impersonate-statusline.sh`. Covers both layouts:
 *  - dev (tsx): apps/cli/src/lib → apps/cli/assets
 *  - built (tsup): apps/cli/dist/<chunk>.js → apps/cli/dist/assets
 *  - installed: <pkg>/dist/<chunk>.js → <pkg>/dist/assets
 *
 * Uses the same walk-up-from-module-dir resolver strategy as other bundled
 * asset/binary lookups in this package.
 */
function resolveBundledAssetPath(): string {
  const moduleDir = dirname(fileURLToPath(import.meta.url));
  const candidates = [
    // Built output: dist/<chunk>.js → dist/assets/...
    join(moduleDir, 'assets', 'impersonate-statusline.sh'),
    // Built output sibling case: dist/<sub>/<chunk>.js → dist/assets/...
    join(moduleDir, '..', 'assets', 'impersonate-statusline.sh'),
    // Dev source: src/lib/impersonate-statusline.ts → ../../assets/...
    join(moduleDir, '..', '..', 'assets', 'impersonate-statusline.sh'),
  ];
  for (const candidate of candidates) {
    if (existsSync(candidate)) return candidate;
  }
  throw new Error(
    `[impersonate-statusline] could not locate bundled asset; tried:\n  ${candidates.join('\n  ')}`,
  );
}

function stripStatusLineKey(settingsPath: string): void {
  const settings = asPlainObject(safeParseJson(readFileSync(settingsPath, 'utf-8')));
  if (!(STATUS_LINE_KEY in settings)) return;
  delete settings[STATUS_LINE_KEY];
  writeFileSync(settingsPath, JSON.stringify(settings, null, 2) + '\n');
}

function safeParseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** Coerce a parsed JSON value to a plain object, or {} for null/array/scalar. */
function asPlainObject(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}
